// Shared schedule formatter utilities
// Minimal MVP to compute day groupings, KPIs, and warnings from scenes + breaks

// Break types we recognize in schedule data
export const BREAK_TYPES = {
  DAY_BREAK: 'DAY_BREAK',
  LUNCH: 'LUNCH',
  COMPANY_MOVE: 'COMPANY_MOVE'
};

// Format pages like "2 3/8"
export function formatPagesInEighths(pages) {
  if (!pages || isNaN(parseFloat(pages))) return '0';
  const pagesNum = parseFloat(pages);
  const pagesInEighths = Math.round(pagesNum * 8);
  const whole = Math.floor(pagesInEighths / 8);
  const eighths = pagesInEighths % 8;
  if (eighths === 0) return `${whole}`;
  if (whole === 0) return `${eighths}/8`;
  return `${whole} ${eighths}/8`;
}

// Basic constraints checker placeholder
export function getDayConstraints(day, options = {}) {
  const warnings = [];
  if (!day) return warnings;
  const maxMoves = options.maxCompanyMovesPerDay ?? 2;
  const moveCount = day.strips?.filter(s => s.type === BREAK_TYPES.COMPANY_MOVE).length || 0;
  if (moveCount > maxMoves) warnings.push({ type: 'COMPANY_MOVES', severity: 'warning', message: `${moveCount} moves > ${maxMoves}` });
  // Long day threshold (hours)
  const longDayHours = options.longDayHours ?? 12;
  if ((day.kpis?.estimatedHours || 0) > longDayHours) {
    warnings.push({ type: 'LONG_DAY', severity: 'warning', message: `${day.kpis.estimatedHours} hrs > ${longDayHours}` });
  }
  // Many locations
  const manyLocations = options.manyLocationsThreshold ?? 3;
  if ((day.kpis?.locations || 0) > manyLocations) {
    warnings.push({ type: 'MANY_LOCATIONS', severity: 'info', message: `${day.kpis.locations} locations > ${manyLocations}` });
  }
  // Lunch presence if day >= 6 hrs and no lunch strip
  const requireLunchAtHours = options.requireLunchAtHours ?? 6;
  const hasLunch = (day.strips || []).some(s => s.type === BREAK_TYPES.LUNCH);
  if ((day.kpis?.estimatedHours || 0) >= requireLunchAtHours && !hasLunch) {
    warnings.push({ type: 'NO_LUNCH', severity: 'warning', message: `No lunch break planned (>= ${requireLunchAtHours} hrs)` });
  }
  // Daylight window: if EXT DAY scenes and estimatedHours exceed daylightWindowHours
  const daylightWindowHours = options.daylightWindowHours; // optional
  const hasExtDay = (day.strips || []).some(s => s.type === 'SCENE' && (s.intExt || '').includes('EXT') && (s.dayNight || '').includes('DAY'));
  if (daylightWindowHours && hasExtDay && (day.kpis?.estimatedHours || 0) > daylightWindowHours) {
    warnings.push({ type: 'DAYLIGHT_WINDOW', severity: 'warning', message: `${day.kpis.estimatedHours} hrs > daylight window ${daylightWindowHours} hrs` });
  }
  return warnings;
}

// Core formatting: split scenes into days based on DAY_BREAKs
export function formatShootingSchedule(scenes = [], breaks = [], options = {}) {
  // Preserve incoming scene order. Break indices (afterSceneIndex) are computed
  // against the original OneLiner order, so re-sorting here will misalign days.
  const sortedScenes = [...scenes];
  const dayBreaks = (breaks || []).filter(b => b.type === BREAK_TYPES.DAY_BREAK);
  const inlineBreaks = (breaks || []).filter(b => b.type === BREAK_TYPES.LUNCH || b.type === BREAK_TYPES.COMPANY_MOVE);

  const minutesPerEighth = typeof options.minutesPerEighth === 'number' ? options.minutesPerEighth : 1.5;
  const minutesOverrideBySceneId = options.minutesOverrideBySceneId || {};
  const lunchMinutesDefault = typeof options.lunchMinutes === 'number' ? options.lunchMinutes : 30;
  const moveMinutesDefault = typeof options.moveMinutes === 'number' ? options.moveMinutes : 20;
  const targetDayMinutes = typeof options.targetDayMinutes === 'number' ? options.targetDayMinutes : 12 * 60; // 12h default
  const dayStartTime = options.dayStartTime; // optional 'HH:MM'

  const addMinutesToHHMM = (hhmm, minutes) => {
    if (!hhmm || typeof minutes !== 'number') return null;
    const [hh, mm] = String(hhmm).split(':').map(n => parseInt(n, 10));
    if (isNaN(hh) || isNaN(mm)) return null;
    let total = (hh * 60 + mm + minutes) % (24 * 60);
    if (total < 0) total += 24 * 60;
    const H = String(Math.floor(total / 60)).padStart(2, '0');
    const M = String(total % 60).padStart(2, '0');
    return `${H}:${M}`;
  };

  // If there are no scenes but we do have day breaks, render empty days structure
  if (!sortedScenes.length && dayBreaks.length) {
    const emptyDays = Array.from({ length: dayBreaks.length + 1 }, (_, i) => {
      const kpis = {
        pages: 0,
        pagesFmt: formatPagesInEighths(0),
        eighths: 0,
        scenes: 0,
        estimatedHours: 0,
        locations: 0,
        companyMoves: 0
      };
      const day = {
        day: i + 1,
        date: null,
        strips: [],
        kpis,
        warnings: getDayConstraints({ strips: [], kpis }, options)
      };
      return day;
    });
    return {
      days: emptyDays,
      totals: {
        scenes: 0,
        pages: 0,
        pagesFmt: formatPagesInEighths(0),
        eighths: 0,
        shootingDays: emptyDays.length,
        avgPagesPerDay: 0
      }
    };
  }

  // Build index set where a day ends (afterSceneIndex)
  const dayEndIdxSet = new Set(dayBreaks.map(b => b.afterSceneIndex));

  // Group inline breaks by afterSceneIndex for quick lookup
  const inlineByAfterIdx = inlineBreaks.reduce((acc, brk) => {
    const key = typeof brk.afterSceneIndex === 'number' ? brk.afterSceneIndex : null;
    if (key === null) return acc;
    acc[key] = acc[key] || [];
    acc[key].push(brk);
    return acc;
  }, {});

  const days = [];
  let dayCounter = 1;
  let currentDayStrips = [];
  let currentDayScenes = [];
  // Track last known, explicit time-of-day to support inheritance (e.g., CONTINUOUS/FOLLOWING/SAME)
  let lastKnownTime = '';

  // Helper: normalize a time-of-day label for consistency
  const normalizeTime = (t) => {
    if (!t) return '';
    return String(t).trim().toUpperCase();
  };

  // Helper: whether the given text indicates time continuation
  const isContinuation = (txt) => {
    if (!txt) return false;
    const s = String(txt).toUpperCase();
    return /\bCONT(?:INUED|INUOUS)?\b/.test(s) || /\bFOLLOWING\b/.test(s) || /\bSAME\b/.test(s);
  };

  sortedScenes.forEach((scene, idx) => {
    // Defensive: skip invalid scene entries to avoid formatter crashing
    if (!scene || typeof scene !== 'object') return;
    // Push the scene strip
    // IMPORTANT: Use only stable ids for sceneId/id so downstream lookups by sceneBreakdown[sceneId] work.
    // Do NOT fall back to scene.number for the id because breakdown maps are keyed by true scene ids.
    const sid = String(scene.id || scene._id || `scene_${idx}`);
    // Compute base fields and effective time-of-day with inheritance
    const rawTime = scene.dayNight || scene.time || scene.tod || '';
    const headingText = scene.heading || scene.slug || scene.scene || '';
    const rawTimeNorm = normalizeTime(rawTime);
    const hasExplicitTime = !!rawTimeNorm && !isContinuation(rawTimeNorm);
    const inheritNeeded = !hasExplicitTime && (isContinuation(rawTimeNorm) || isContinuation(headingText) || !rawTimeNorm);
    const effectiveTime = normalizeTime(hasExplicitTime ? rawTimeNorm : (inheritNeeded ? (lastKnownTime || rawTimeNorm) : rawTimeNorm));
    if (effectiveTime && !isContinuation(effectiveTime)) {
      lastKnownTime = effectiveTime; // update only on explicit/non-continuation
    }
    const sceneStrip = {
      type: 'SCENE',
      id: sid,
      sceneId: sid,
      slug: scene.slug || scene.scene || `Scene ${scene.number ?? ''}`,
      number: scene.number ?? scene.sceneNumber?.standard ?? scene.sceneNumber?.original ?? scene.sceneNumber ?? '',
      pages: parseFloat(scene.pages) || 0,
      intExt: scene.intExt || scene.location_type || scene.locationType || '',
      dayNight: rawTime,
      effectiveDayNight: effectiveTime,
      set: scene.set || scene.area || scene.setName || '',
      location: scene.location || scene.locationName || scene.setLocation || '',
      castIds: scene.castIds || [],
      flags: scene.flags || [],
      estimatedMinutes: (() => {
        const override = minutesOverrideBySceneId[sid];
        if (typeof override === 'number' && override >= 0) return Math.round(override);
        return Math.max(0, Math.round(((parseFloat(scene.pages) || 0) * 8) * minutesPerEighth));
      })()
    };
    currentDayStrips.push(sceneStrip);
    currentDayScenes.push(scene);

    // Insert inline breaks that occur AFTER this scene index
    const ib = inlineByAfterIdx[idx] || [];
    ib.forEach(brk => {
      const durationMinutes = typeof brk.durationMinutes === 'number'
        ? brk.durationMinutes
        : (brk.type === BREAK_TYPES.LUNCH ? lunchMinutesDefault : moveMinutesDefault);
      currentDayStrips.push({ type: brk.type, label: brk.type === BREAK_TYPES.LUNCH ? 'Lunch' : 'Company Move', durationMinutes });
    });

    const isDayEnd = dayEndIdxSet.has(idx) || idx === sortedScenes.length - 1;
    if (isDayEnd) {
      // Compute KPIs for the current day
      const pagesSum = currentDayScenes.reduce((sum, s) => sum + (parseFloat(s.pages) || 0), 0);
      const eighths = Math.round(pagesSum * 8);
      const companyMoves = currentDayStrips.filter(s => s.type === BREAK_TYPES.COMPANY_MOVE).length;
      const sceneMinutes = currentDayStrips.filter(s => s.type === 'SCENE').reduce((m, s) => m + (s.estimatedMinutes || 0), 0);
      const breakMinutes = currentDayStrips.filter(s => s.type !== 'SCENE').reduce((m, s) => m + (s.durationMinutes || 0), 0);
      const estimatedMinutes = sceneMinutes + breakMinutes;
      const progress = targetDayMinutes ? Math.max(0, Math.min(100, Math.round((estimatedMinutes / targetDayMinutes) * 100))) : null;
      const wrapTime = dayStartTime ? addMinutesToHHMM(dayStartTime, estimatedMinutes) : null;
      const kpis = {
        pages: pagesSum,
        pagesFmt: formatPagesInEighths(pagesSum),
        eighths,
        scenes: currentDayScenes.filter(Boolean).length,
        estimatedMinutes,
        estimatedHours: Math.round((estimatedMinutes / 60) * 10) / 10,
        locations: new Set(currentDayScenes.map(s => s.location).filter(Boolean)).size,
        companyMoves,
        progress,
        wrapTime,
        targetDayMinutes
      };
      const day = {
        day: dayCounter,
        date: null,
        strips: currentDayStrips,
        kpis,
        warnings: getDayConstraints({ strips: currentDayStrips, kpis }, options)
      };
      days.push(day);
      // reset for next day
      currentDayStrips = [];
      currentDayScenes = [];
      // Reset time-of-day inheritance at day boundary
      lastKnownTime = '';
      dayCounter += 1;
    }
  });

  const totalsPages = sortedScenes.reduce((sum, s) => sum + (parseFloat(s.pages) || 0), 0);
  const totalsEighths = Math.round(totalsPages * 8);

  return {
    days,
    totals: {
      scenes: sortedScenes.length,
      pages: totalsPages,
      pagesFmt: formatPagesInEighths(totalsPages),
      eighths: totalsEighths,
      shootingDays: Math.max(1, days.length),
      avgPagesPerDay: days.length ? (totalsPages / days.length) : 0
    }
  };
}
